import {ErrorMessage} from "@hookform/error-message";
import {Box, FormControlLabel, Paper, Typography, useMediaQuery, useTheme} from "@mui/material";
import {RhfCheckbox, RhfTextField} from "mui-rhf-integration";
import {useMemo} from "react";
import {type Control, useFormState} from "react-hook-form";
import Markdown from "react-markdown";
import DigitalSignatureField from "@/components/Forms/Fields/DigitalSignatureField.tsx";
import type {CourseQuestion} from "@/components/Providers/ProgramsProvider.tsx";

export const WAIVER = "Waiver";
export const SIGNATURE = "Signature";
export const FREE_FORM = "Free Form";
export const VALUE_LIST = "Value List";

type Props = {
    control: Control;
    courseQuestions: CourseQuestion[];
};

const WaiverQuestion = ({courseQuestions, control}: Props) => {
    const formState = useFormState();
    const {waiver, signature, legalName} = useMemo(() => {
        return {
            waiver: courseQuestions.find(q => q.responseType === WAIVER),
            signature: courseQuestions.find(q => q.responseType === SIGNATURE),
            legalName: courseQuestions.find(q => q.responseType === FREE_FORM),
        };
    }, [courseQuestions]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Box
            sx={{
                mb: 3,
                p: 2,
                backgroundColor: "primary.50",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "grey.300",
            }}
        >
            <Typography variant="h6" gutterBottom color="primary.main">
                Parent/Guardian Waiver Agreement
            </Typography>
            {waiver !== undefined && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 2,
                        maxHeight: 300,
                        overflowY: "auto",
                        backgroundColor: "white",
                        borderColor: "grey.300",
                    }}
                >
                    <Typography variant="body2">
                        <Markdown>{waiver?.question}</Markdown>
                    </Typography>
                </Paper>
            )}
            {waiver !== undefined && (
                <Box>
                    <FormControlLabel
                        control={<RhfCheckbox control={control} name={waiver.id} />}
                        label="I agree to the above waiver Terms & Conditions *"
                    />
                    <ErrorMessage
                        errors={formState.errors}
                        name={waiver.id}
                        render={() => (
                            <Typography sx={{color: "red"}}>
                                You must agree to the above waiver Terms & Conditions
                            </Typography>
                        )}
                    />
                </Box>
            )}
            <Box
                sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: "white",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                }}
            >
                {signature !== undefined && (
                    <Box>
                        <DigitalSignatureField
                            name={signature?.id}
                            label="Parent/Guardian Digital Signature"
                            width={isMobile ? 300 : 780}
                        />
                    </Box>
                )}
                {legalName !== undefined && (
                    <Box
                        sx={{
                            mt: 3,
                        }}
                    >
                        <RhfTextField
                            label="Parent/Guardian Legal Name"
                            control={control}
                            name={legalName.id}
                            fullWidth
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default WaiverQuestion;
